/**
 * PATH: apps/ai/src/services/streaming/dataApis.ts
 * PURPOSE: Portfolio metrics, smart alerts, and receivables forecast APIs
 */

import { API_BASE } from '@quotech/api';
import { buildOrganisationHeader, getAccessToken } from './auth';
import type { PortfolioMetrics, SmartAlert, ReceivablesForecast, ReceivableItem } from './types';

export async function fetchPortfolioMetrics(): Promise<PortfolioMetrics | null> {
  const token = getAccessToken();
  if (!token) { console.warn('[aiStreaming] No token, skipping portfolio metrics'); return null; }
  try {
    const response = await fetch(`${API_BASE}/api/v2/jobs/summary/`, { headers: { Authorization: `Bearer ${token}`, ...buildOrganisationHeader() } });
    if (!response.ok) { console.error('[aiStreaming] Failed to fetch portfolio metrics:', response.status); return null; }
    const data = await response.json();
    const contractValue = data.contract_value || 0;
    const grossProfit = data.gross_profit || 0;
    const wip = data.wip || 0;
    const atRisk = data.health_distribution?.atRisk || data.jobs_at_risk || 0;
    const avgMargin = contractValue > 0 ? (grossProfit / contractValue) * 100 : 0;
    return { activeJobs: data.total_jobs || 0, atRisk, outstanding: wip, avgMargin, contractValue, totalInvoiced: data.total_invoiced || 0, wip, labourCost: data.labour_cost || 0, materialsCost: data.materials_cost || 0, totalCosts: data.total_costs || 0, grossProfit };
  } catch (error) { console.error('[aiStreaming] Error fetching portfolio metrics:', error); return null; }
}

async function fetchFallbackAlerts(token: string): Promise<SmartAlert[]> {
  try {
    const response = await fetch(`${API_BASE}/api/report/notification/?limit=5`, { headers: { Authorization: `Bearer ${token}`, ...buildOrganisationHeader() } });
    if (!response.ok) return [];
    const data = await response.json();
    return (data.results || data || []).slice(0, 5).map((n: { title?: string; message?: string; job_name?: string }) => ({ type: 'info' as const, message: n.title || n.message || 'Notification', jobName: n.job_name }));
  } catch { return []; }
}

export async function fetchSmartAlerts(): Promise<SmartAlert[]> {
  const token = getAccessToken();
  if (!token) return [];
  try {
    const response = await fetch(`${API_BASE}/api/report/notification/smart/?limit=5`, { headers: { Authorization: `Bearer ${token}`, ...buildOrganisationHeader() } });
    if (!response.ok) return await fetchFallbackAlerts(token);
    const data = await response.json();
    return (data.results || data.alerts || []).slice(0, 5).map((alert: { priority?: string; severity?: string; type?: string; notification_type?: string; message?: string; title?: string; job_name?: string; job_id?: string | number }) => {
      let alertType: 'warning' | 'critical' | 'info' = 'info';
      const p = (alert.priority || alert.severity || alert.type || alert.notification_type || '').toLowerCase();
      if (p.includes('critical') || p.includes('urgent')) alertType = 'critical';
      else if (p.includes('warn') || p.includes('high')) alertType = 'warning';
      return { type: alertType, message: alert.message || alert.title || 'New notification', jobName: alert.job_name, jobId: alert.job_id };
    });
  } catch (err) { console.warn('[aiStreaming] Smart alerts fetch failed:', err); return []; }
}

export async function fetchReceivablesForecast(): Promise<ReceivablesForecast | null> {
  const token = getAccessToken();
  if (!token) { console.warn('[aiStreaming] No token, skipping receivables forecast'); return null; }
  try {
    const response = await fetch(`${API_BASE}/api/report/receivables-forecast/`, { headers: { Authorization: `Bearer ${token}`, ...buildOrganisationHeader() } });
    if (!response.ok) { console.error('[aiStreaming] Failed to fetch receivables forecast:', response.status); return null; }
    const data = await response.json();
    const receivablesList: ReceivableItem[] = (data.receivables_list || []).map((item: any) => ({ invoiceNumber: item.invoice_number || '', jobId: item.job_id || '', client: item.client || 'Unknown', amount: item.amount || 0, dueDate: item.due_date || '', daysOverdue: item.days_overdue || 0, status: item.status === 'Overdue' ? 'Overdue' : 'Due Soon' as const }));
    const forecastChart = (data.forecast_chart || []).map((i: any) => ({ week: i.week || '', amount: i.amount || 0 }));
    const rawInsights = Array.isArray(data.ai_insights) ? data.ai_insights : (data.ai_insights?.recommendations || []);
    const insights = rawInsights.map((i: any) => ({ type: i.type || 'info', message: i.message || '', impact: i.impact || '' }));
    return { totalOutstanding: data.summary?.total_outstanding || 0, overdueAmount: data.summary?.overdue_amount || 0, collectionRate: data.summary?.collection_rate || 0, forecastNext30Days: data.summary?.forecast_next_30_days || 0, receivablesList, forecastChart, insights };
  } catch (error) { console.error('[aiStreaming] Error fetching receivables forecast:', error); return null; }
}
